// Full-exhaustion tracking fetch used by the contract-overview summary hooks
// (health + performance), FinanceTab, and MilestoneTab. Pages through nextToken
// until the backend returns no token. Archived rows are excluded unless
// opts.includeArchived is set; the flag is part of the cache key so the two
// variants never serve each other's data.
//
// Callers share the ["tracking", contractId, type, "flat-all", archived] cache
// key ("flat-all" distinguishes this from useAllDeliverablesForSummary's
// "summary-all" key which has incompatible semantics), so mounting several
// consumers on the same contract issues one request per (type, archived) pair.
//
// Drain guards: the accumulated list feeds finance-bearing sums, so a partial
// or duplicated result must never be cached as success. A repeated nextToken
// or an exhausted iteration cap rejects the query (React Query error state —
// the same state a network failure on a later page produces), and items are
// deduplicated by id in case a row straddles a page boundary under concurrent
// writes.
//
// Kept in its own module (rather than in useTracking) so that the many
// component tests which vi.mock("../../hooks/useTracking") with an explicit
// factory do not need to also stub this hook.

import { useQuery } from "@tanstack/react-query";
import { trackingApi } from "../services/api";
import { trackingKeys } from "./trackingKeys";
import type { TrackingItem } from "../types";

const MAX_PAGE_ITERATIONS = 50;

export interface FlatTrackingListOptions {
  includeArchived?: boolean;
}

export function useFlatTrackingList<T extends TrackingItem>(
  contractId: string,
  type: T["type"],
  opts?: FlatTrackingListOptions,
) {
  const includeArchived = opts?.includeArchived ?? false;
  return useQuery({
    queryKey: trackingKeys.flatAll(contractId, type, includeArchived),
    queryFn: async () => {
      const accumulated = new Map<string, T>();
      const seenTokens = new Set<string>();
      let nextToken: string | undefined;
      let iterations = 0;
      do {
        if (iterations++ >= MAX_PAGE_ITERATIONS) {
          throw new Error(
            `Tracking list for ${type} exceeded ${MAX_PAGE_ITERATIONS} pages without completing`,
          );
        }
        const page = await trackingApi.list(contractId, type, {
          limit: 200,
          nextToken,
          includeArchived,
        });
        for (const item of page.items) {
          if (item.type === type) {
            accumulated.set(item.id, item as T);
          }
        }
        nextToken = page.nextToken;
        if (nextToken) {
          if (seenTokens.has(nextToken)) {
            throw new Error(
              `Tracking list for ${type} returned a repeated page token; refusing a duplicated result`,
            );
          }
          seenTokens.add(nextToken);
        }
      } while (nextToken);
      return [...accumulated.values()];
    },
    enabled: !!contractId,
    // Tracking mutations push-invalidate this key (it sits under the
    // ["tracking", contractId, type] prefix), so a 30s staleTime only
    // suppresses redundant full-drain refetches on remount.
    staleTime: 30_000,
  });
}
